import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * VoiceRecorder
 * - Records via MediaRecorder
 * - Sends to /api/voice/transcribe (Whisper) and optionally persists the audio
 * - Calls onTranscribed(text, audioPath) with the resulting transcript + optional audio path
 */
export const VoiceRecorder = ({ onTranscribed, disabled, saveAudio = true }) => {
  const [state, setState] = useState("idle"); // idle | recording | transcribing
  const [error, setError] = useState("");
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => () => {
    try { mediaRef.current?.stream?.getTracks().forEach((t) => t.stop()); } catch (e) {}
  }, []);

  const start = async () => {
    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (e) {}
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setState("transcribing");
        try {
          const form = new FormData();
          form.append("audio", blob, "recording.webm");
          const url = saveAudio ? "/voice/transcribe?save_audio=1" : "/voice/transcribe";
          const res = await api.post(url, form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const text = (res.data?.text || "").trim();
          const audioPath = res.data?.audio_path || null;
          if (text || audioPath) onTranscribed?.(text, audioPath);
        } catch (e) {
          setError("Sorry, we couldn't transcribe that. Try again?");
        } finally {
          setState("idle");
        }
      };
      mediaRef.current = mr;
      mr.start();
      setState("recording");
    } catch (e) {
      setError("Microphone permission needed.");
      setState("idle");
    }
  };

  const stop = () => {
    try { mediaRef.current?.stop(); } catch (e) {}
  };

  const isRecording = state === "recording";
  const isBusy = state === "transcribing";

  return (
    <div className="flex flex-col items-center gap-3">
      <button
        type="button"
        onClick={isRecording ? stop : start}
        disabled={disabled || isBusy}
        aria-label={isRecording ? "Stop recording" : "Start voice recording"}
        data-testid="voice-record-button"
        className={`flex items-center justify-center transition-all duration-500 hairline ${
          isRecording ? "animate-pulse-record" : ""
        }`}
        style={{
          width: 76, height: 76, borderRadius: 999,
          background: isRecording ? "#B8973A" : "#FFFFFF",
          color: isRecording ? "#FAF7F2" : "#111D33",
          borderColor: isRecording ? "#B8973A" : "#E8D5A3",
        }}
      >
        {isBusy ? <Loader2 className="animate-spin" size={28} /> :
          isRecording ? <Square size={26} fill="currentColor" /> : <Mic size={26} />}
      </button>
      <div className="label-ui text-[11px] text-[#8C8278]" data-testid="voice-status">
        {isBusy ? "Reflecting on your story…" :
         isRecording ? "Listening… speak naturally." :
         "Tap to record"}
      </div>
      {error && (
        <p className="text-sm text-red-700 mt-1" data-testid="voice-error">{error}</p>
      )}
    </div>
  );
};

export default VoiceRecorder;
